import { handleResponseLanguage } from '../../../../../../i18n';
import { FormOption } from '../../../../../../types/Form';
import store from '../../../store/redux/store';
import { Inputs } from '../../../types/Enums';
import {
  EnumObject,
  FlowsQuestion,
  FlowsQuestionAnswer,
  FlowsQuestionnaire,
} from '../../../types/FlowsTypes';
import { handleConditions } from '../../../utils/flowsQuestionnaireUtils';
import {
  flattenQuestionaire,
  getSortedQuestionOptions,
} from '../../../utils/flowsQuestionsUtils';

export const generateAnswers = (
  questionnaire: FlowsQuestionnaire,
): FlowsQuestionAnswer[] => {
  const answers: FlowsQuestionAnswer[] = [];
  questionnaire.groups.forEach(({ id: groupId, questions }) => {
    // Extra data
    if (questions.find(({ id }) => id === 'car.catalogue_value')) {
      answers.push({
        id: 'car.catalogue_value',
        answer: 35000,
        groupId,
      });
    }

    questions
      .filter(({ rules }) => !!rules.mandatory)
      .forEach((question) => {
        const answer = generateAnswer(question, groupId);
        if (
          handleConditions(
            flattenQuestionaire(questionnaire),
            answers,
            question.conditions,
          )
        )
          answers.push(answer);
      });
  });

  return answers;
};

const questionnaire = store.getState().flows.questionnaire;
const answers = store.getState().flows.answers;

/** Prepare the enum before returning */
const prepareEnum = (enumOptions: EnumObject[]) => {
  let _enumOptions = [...enumOptions];

  _enumOptions = _enumOptions.filter((enumOption) =>
    handleConditions(
      flattenQuestionaire(questionnaire),
      answers,
      enumOption.conditions,
    ),
  );

  return _enumOptions;
};

/** Prepare the options object array before returning */
const prepareOptions = (
  options: FormOption[],
  questionSort?: 'asc' | 'desc' | null,
) => {
  let _options = [...options];

  _options = getSortedQuestionOptions(_options, questionSort);

  return _options;
};

const getQuestionEnumValues = (question: FlowsQuestion) => {
  if (question.rules.enum) {
    const options = prepareEnum(question.rules.enum as EnumObject[]).map(
      (option) => {
        return {
          label: handleResponseLanguage(option.label) || option.value,
          value: option.value,
        };
      },
    );

    return prepareOptions(options, question.rules.sort);
  }
};

export const generateAnswer = (
  question: FlowsQuestion,
  groupId: string,
): FlowsQuestionAnswer => {
  let answer = question.value;

  if (answer == null) {
    switch (question.id) {
      case 'policy_holder.first_name':
        answer = 'WeGroup';
        break;
      case 'policy_holder.last_name':
        answer = 'Test';
        break;
      case 'policy_holder.email':
        answer = 'test@wegroup.be';
        break;
      case 'policy_holder.birth':
        answer = '1990-01-01T00:00:00+02:00';
        break;
      case 'driver.same_as_policy_holder':
        answer = true;
        break;
      case 'home.attachment':
        answer = 'APARTMENT';
        break;
      case 'company.cbe':
        answer = '0680957816';
        break;
      case 'specs.benefit_1':
        answer = 5000;
        break;
      case 'specs.payment_until_1':
        answer = 40;
        break;
      case 'specs.bonus_malus':
        answer = 0;
        break;
      default: {
        switch (question.rules.input_type) {
          case Inputs.INPUT:
            if (question.rules.data_type === 'NUMBER') answer = 0;
            else answer = 'test';
            break;
          case Inputs.SELECT:
            answer = getQuestionEnumValues(question)?.[0].value;
            break;
          case Inputs.DATE:
            answer = '2020-01-01T00:00:00+02:00';
            break;
          case Inputs.CHECKBOX:
            answer = false;
            break;
          case Inputs.SINGLE_CHECKBOX:
            answer = false;
            break;
          case Inputs.ADDRESS:
            answer = {
              country_code: 'BE',
              street: 'Moutstraat',
              boxnr: null,
              housenr: '68',
              zipcode: '9000',
              city: 'Gent',
              lat: 51.02987885537655,
              lng: 3.73746650732775,
              region: 'Oost-Vlaanderen',
              _local: {
                self: 'https://api.staging.wegroup.be/v1/api/address/info/premium?street=Moutstraat&country_code=BE&zipcode=9000&housenr=68&boxnr=None&city=Gent&lat=51.02987885537655&lng=3.73746650732775&region=Oost-Vlaanderen',
              },
            };
            break;
          case Inputs.TELEPHONE_NUMBER:
            answer = '+32499999999';
            break;
          case Inputs.CAR:
            answer = {
              color_hex: null,
              series: '3 SERIES',
              images: [
                'https://upload.wikimedia.org/wikipedia/commons/thumb/1/16/2019_BMW_330i_M_Sport.jpg/1200px-2019_BMW_330i_M_Sport.jpg',
                'https://autotijd.be/images/bmw/2020/3-reeks-berline/prijzen/bmw-3-reeks-berline-2020-prijzen-02.jpg',
                'https://autotijd.be/images/bmw/2020/3-reeks-berline/prijzen/bmw-3-reeks-berline-2020-prijzen-01.jpg',
                'https://www.bmw.be/content/dam/bmw/common/all-models/3-series/sedan/2021/navigation/bmw-3-series-modelfinder-sport-line-330e.png',
                'https://static.moniteurautomobile.be/imgcontrol/images_tmp/clients/moniteur/c680-d465/content/medias/images/cars/bmw/series-3/bmw--serie-3-touring--2020/bmw--serie-3-touring--2020-m-1.jpg',
              ],
              id: 'cbd9a002-46c6-4e3b-96cf-c8e2cd4dba45',
              motor_type: 'DIE',
              registration_first: '2020-07-17T00:00:00+02:00',
              kw: 90,
              cc: 1995,
              model: '3 SERIES',
              model_year: 2020,
              version: '316D90 MHEV STEPTRONIC',
              brand: 'BMW',
              name: 'BMW 3 SERIES 316D90 MHEV STEPTRONIC',
              color: null,
              category: 'SEDAN',
              co2: 99,
              is_sportscar: null,
              seats: 5,
              price: 30330,
              vehicleChoices: {
                brand: {
                  label: 'BMW',
                  value: {
                    key: 'bmw',
                    logo: 'https://files.wegroup.be/logos/vehicle/bmw.svg',
                  },
                },
                model: {
                  label: '3 SERIES',
                  value: {
                    brandKey: 'bmw',
                    id: '02e54d25-2a39-4d77-9e06-0f7cf49c94c3',
                    name: '3 SERIES',
                    model: '3 SERIES',
                  },
                },
                selected: 'cbd9a002-46c6-4e3b-96cf-c8e2cd4dba45',
              },
            };
            break;
          case Inputs.ACCIDENT:
            answer = [
              {
                at_fault: true,
                date: '2020-07-17T00:00:00+02:00',
              },
            ];
            break;
          case Inputs.HOME:
            answer = {
              address: {
                street: 'Moutstraat',
                country_code: 'BE',
                zipcode: '9000',
                housenr: '68',
                boxnr: null,
                city: 'Gent',
                lat: 51.029878860000004,
                lng: 3.73746652,
                region: 'Oost-Vlaanderen',
              },
              cellar: 'NONE',
              attic: 'NONE',
              _local: {
                self: 'https://api.staging.wegroup.be/v1/api/address/info/premium?street=Moutstraat&country_code=BE&zipcode=9000&housenr=68&boxnr=None&city=Gent&lat=51.02987885537655&lng=3.73746650732775&region=Oost-Vlaanderen',
                coordinates: [
                  [
                    {
                      lng: 3.737940805858091,
                      lat: 51.03011613103431,
                    },
                    {
                      lng: 3.737782646366429,
                      lat: 51.02996776742912,
                    },
                    {
                      lng: 3.7373252031811184,
                      lat: 51.02968315063352,
                    },
                    {
                      lng: 3.7370960382455505,
                      lat: 51.02982858792179,
                    },
                    {
                      lng: 3.737102607732417,
                      lat: 51.029832469697524,
                    },
                    {
                      lng: 3.737295422029578,
                      lat: 51.0299529271403,
                    },
                    {
                      lng: 3.7372596940475904,
                      lat: 51.02997788286264,
                    },
                    {
                      lng: 3.7372635890922843,
                      lat: 51.029980100291084,
                    },
                    {
                      lng: 3.7372741697036864,
                      lat: 51.02997271006568,
                    },
                    {
                      lng: 3.7373636906284444,
                      lat: 51.030028551246964,
                    },
                    {
                      lng: 3.737352454782645,
                      lat: 51.030035954079416,
                    },
                    {
                      lng: 3.737356322711706,
                      lat: 51.030038288724185,
                    },
                    {
                      lng: 3.7373942534780835,
                      lat: 51.030014388397134,
                    },
                    {
                      lng: 3.7375947079287184,
                      lat: 51.03013955438041,
                    },
                    {
                      lng: 3.7375968116156373,
                      lat: 51.030138213648755,
                    },
                    {
                      lng: 3.7376150516088105,
                      lat: 51.030149576024286,
                    },
                    {
                      lng: 3.737615915256549,
                      lat: 51.030150482746,
                    },
                    {
                      lng: 3.737613776952721,
                      lat: 51.030151684065885,
                    },
                    {
                      lng: 3.7376119771168104,
                      lat: 51.03015308970963,
                    },
                    {
                      lng: 3.737610561905247,
                      lat: 51.03015465965228,
                    },
                    {
                      lng: 3.7376095683361457,
                      lat: 51.03015635497037,
                    },
                    {
                      lng: 3.7376090226044116,
                      lat: 51.030158127477534,
                    },
                    {
                      lng: 3.7376089398951535,
                      lat: 51.03015993353103,
                    },
                    {
                      lng: 3.737609324523874,
                      lat: 51.03016172367658,
                    },
                    {
                      lng: 3.7376101625291343,
                      lat: 51.03016345066354,
                    },
                    {
                      lng: 3.7376114307957935,
                      lat: 51.03016506949322,
                    },
                    {
                      lng: 3.737613100719556,
                      lat: 51.030166536288476,
                    },
                    {
                      lng: 3.7376151271818974,
                      lat: 51.03016781398739,
                    },
                    {
                      lng: 3.7376174540702887,
                      lat: 51.03016886892098,
                    },
                    {
                      lng: 3.737620019752161,
                      lat: 51.03016967084289,
                    },
                    {
                      lng: 3.7376227588063244,
                      lat: 51.03017019984191,
                    },
                    {
                      lng: 3.737625596595815,
                      lat: 51.03017044286159,
                    },
                    {
                      lng: 3.7376284602459555,
                      lat: 51.03017039145714,
                    },
                    {
                      lng: 3.737631273078502,
                      lat: 51.03017004867002,
                    },
                    {
                      lng: 3.737633960146431,
                      lat: 51.0301694244546,
                    },
                    {
                      lng: 3.737648087118245,
                      lat: 51.03018425467805,
                    },
                    {
                      lng: 3.7376492971329967,
                      lat: 51.03018552455313,
                    },
                    {
                      lng: 3.737747501132051,
                      lat: 51.03016273038021,
                    },
                    {
                      lng: 3.737746926847985,
                      lat: 51.03016174466407,
                    },
                    {
                      lng: 3.7377344075415855,
                      lat: 51.030139111400565,
                    },
                    {
                      lng: 3.737802070215981,
                      lat: 51.030124266808194,
                    },
                    {
                      lng: 3.737815928357671,
                      lat: 51.030146667898705,
                    },
                    {
                      lng: 3.737940805858091,
                      lat: 51.03011613103431,
                    },
                  ],
                ],
              },
            };
            break;
          case Inputs.PERCENTAGE:
            answer = 0;
            break;
          case Inputs.CURRENCY:
            answer = 30000;
            break;
          case Inputs.ANNEXES:
            answer = [
              {
                type: 'GARDEN_SHED',
                surface: 20,
              },
            ];
            break;
          case Inputs.ROOM:
            answer = 0;
            break;
          case Inputs.SECURITY_SYSTEM:
            answer = true;
            break;
          case Inputs.MULTISELECT:
            answer = [getQuestionEnumValues(question)?.[0].value];
            break;
          case Inputs.SLIDER:
            answer = 'BETWEEN_15000_AND_20000';
            break;
          case Inputs.ICON_SELECT:
            answer = getQuestionEnumValues(question)?.[0].value;
            break;
          case Inputs.SELECT_DETAILED:
            answer = getQuestionEnumValues(question)?.[0].value;
            break;
          case Inputs.ADDITIONAL_DRIVER:
            answer = {};
            break;
          case Inputs.LINK:
            answer = [
              {
                key: 'driversLicense',
                location:
                  'amazon_s3_bucket:wg-broker-avatars:/distributions/36d87970-b81d-4425-bb7f-f23270f13637/Louise_Face-Circle.png_9c5d91ea-dbe0-4a0d-bd67-42b2a56d1c93',
                filename: 'Louise_Face-Circle.png',
                size: 25784,
                filetype: 'image/png',
              },
            ];
            break;
          case Inputs.MASKED:
            answer = question.rules.mask;
            break;
          case Inputs.COMPANY_SEARCH:
            answer = {
              _local: {
                id: null,
                email: null,
                native_language: null,
                telephonenr: null,
                iban: null,
                address: {
                  street: 'Moutstraat',
                  country_code: 'BE',
                  zipcode: '9000',
                  housenr: '68',
                  boxnr: null,
                  city: 'Gent',
                  lat: null,
                  lng: null,
                  region: null,
                },
                name: 'WeGroup',
                cbe: '0680957816',
                company_registration: {
                  type: 'CBE',
                  nr: '0680957816',
                },
                rsz_nr: null,
                founding_date: '2017-09-12',
                legal_form: 'NV',
                commercial_name: null,
                main_activity:
                  'Verzekeringsagenten en -makelaars (BTW-activiteiten)|Verzekeringsagenten en -makelaars (RSZ-activiteiten)|',
                website: null,
              },
              'company.name': 'WeGroup',
              'company.cbe': '0680957816',
              'company.address': {
                street: 'Moutstraat',
                country_code: 'BE',
                zipcode: '9000',
                housenr: '68',
                boxnr: null,
                city: 'Gent',
                lat: null,
                lng: null,
                region: null,
              },
            };
            break;
          case Inputs.RADIO:
            answer = false;
            break;
          default:
            break;
        }
      }
    }
  }
  return {
    id: question.id,
    answer,
    groupId,
  };
};
